import { useState, useEffect, useContext } from "react";
import Axios from "axios";
import { AddIcon } from "../Icons/AddIcon";
import { CheckIcon } from "../Icons/CheckIcon";
import { ChevronDownIcon } from "../Icons/ChevronDownIcon";
import ModalUsados from "../Modals/ModalUsados";
import ModalAccessories from "../Modals/ModalAccessories";
import ModalMaintenancePlans from "../Modals/ModalMaintenancePlans";
import useConfiguratorData from "../../hooks/useConfiguratorData";
import useAccessoriesData from "../../hooks/useAccessoriesData";
import useFinanciadoData from "../../hooks/useFinanciadoData";
import { ConfiguratorContext } from "../../context";
import { CurrencyFormat } from "../../../utils";

export default function ItemsSelector({ isMAF = false }) {
    const {
        getPrepaidMaintenancePlans,
        getSelectedPrepaidMaintenancePlan,
        setSelectedPrepaidMaintenancePlan,
        getSelectedVersion,
    } = useConfiguratorData();

    const {
        getAddedAccessories,
        setIgnoreAccessories,
        getAccessories,
        setAccessories,
        setFilteredAccessories,
        setCategories,
    } = useAccessoriesData();
    const { getUsado } = useFinanciadoData();

    // Acceso al boolean accessories desde el contexto
    const [state] = useContext(ConfiguratorContext);
    const accessoriesEnabled = state.accessoriesEnabled;

    const [isExpanded, setIsExpanded] = useState(false);
    const [modalUsadosIsOpen, setModalUsadosIsOpen] = useState(false);
    const [modalAccessoriesIsOpen, setModalAccessoriesIsOpen] = useState(false);
    const [modalMaintenancePlansIsOpen, setModalMaintenancePlansIsOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [accessoriesLoaded, setAccessoriesLoaded] = useState(false);

    const prepaidMaintenancePlans = getPrepaidMaintenancePlans();
    const selectedPrepaidMaintenancePlan = getSelectedPrepaidMaintenancePlan();
    const addedAccessories = getAddedAccessories();
    const accessories = getAccessories();
    const selectedVersion = getSelectedVersion();
    const usado = getUsado();

    useEffect(() => {
        if (addedAccessories.length) setIgnoreAccessories(false);
        else setIgnoreAccessories(true);
    }, [addedAccessories.length]);

    useEffect(() => {
        setAccessoriesLoaded(false);
        setLoading(true);
    }, [selectedVersion?.id]);

    useEffect(() => {
        if (!accessoriesLoaded && selectedVersion?.id && !accessories.length) {
            setAccessoriesLoaded(true);
            Axios.get(`/api/vehicles/bnp/accessories/${selectedVersion.id}/`)
                .then((response) => {
                    let data = response.data.map((element) => {
                        let copy = { ...element };
                        copy.is_added = false;
                        return copy;
                    });
                    let accessoriesCategories = new Set();
                    accessoriesCategories.add("Todos");
                    data.forEach((accessory) => {
                        accessoriesCategories.add(accessory.category);
                    });
                    setCategories([...accessoriesCategories]);
                    setAccessories(data);
                    setFilteredAccessories(data);
                })
                .catch((error) => {
                    console.error(error);
                })
                .finally(() => {
                    setLoading(false);
                });
        } else if (accessories.length > 0) {
            setLoading(false);
        }
    }, [selectedVersion?.id, accessoriesLoaded, accessories.length]);

    const toggleExpanded = () => {
        setIsExpanded(!isExpanded);
    };

    const handleAccessoriesClick = () => {
        setModalAccessoriesIsOpen(true);
        setIgnoreAccessories(false);
        if(dataLayer) {
            dataLayer.push({
                'event': 'cotizador',
                'eventCategory': 'mas_opciones',
                'eventAction': 'cta',
                'eventLabel': 'accesorios'
            });
            
        }
    };

    const handleUsedCarClick = () => {
        setModalUsadosIsOpen(true);

        if(dataLayer) {
            dataLayer.push({
                'event': 'cotizador',
                'eventCategory': 'mas_opciones',
                'eventAction': 'cta',
                'eventLabel': 'toma_de_usado'
            });
            dataLayer.push({
                'event': 'toma_de_usado',
                'step_id': 'step_1',
                'step_name': 'iniciar',
            });
        }
    };

    const handlePrepaidMaintenanceClick = () => {
        setModalMaintenancePlansIsOpen(true);
    };

    return (
        <div className="items-selector">
            <div
                className={"visible-part" + (isExpanded ? " expanded" : "")}
                onClick={toggleExpanded}
                style={{ cursor: "pointer" }}
            >
                <div>
                    <h3>¿Quieres agregar algo más?</h3>
                    <p>Completa tu cotización seleccionando las opciones a tu medida</p>
                </div>
                <ChevronDownIcon />
            </div>

            <div className={`hidden-part ${isExpanded ? "active" : ""}`}>
                {/* Toyota Accesorios Originales - Solo mostrar si hay accesorios disponibles */}
                {accessoriesEnabled && accessories.length > 0 && (
                    <div className="selector-item">
                        <div className="item-details">
                            <div>
                                <p className="title">Toyota Accesorios Originales</p>
                                <p>Elige los accesorios que te gustaría agregar a tu vehículo</p>
                            </div>
                            <button
                                onClick={handleAccessoriesClick}
                                style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    padding: 0,
                                }}
                                aria-label="Seleccionar accesorios originales"
                            >
                                {addedAccessories.length > 0 ? <CheckIcon /> : <AddIcon />}
                            </button>
                        </div>
                    </div>
                )}

                {/* Mantenciones Prepagadas solo para MAF */}
                {isMAF && prepaidMaintenancePlans && prepaidMaintenancePlans.length > 0 && (
                    <div className="selector-item">
                        <div className="item-details">
                            <div>
                                <p className="title">Mantenciones Prepagadas</p>
                                <p>
                                    {prepaidMaintenancePlans.length > 1 
                                        ? `Elige entre ${prepaidMaintenancePlans.length} planes disponibles` 
                                        : "Ahorra comprando tus mantenciones por adelantado"
                                    }
                                </p>
                            </div>
                            <button
                                onClick={handlePrepaidMaintenanceClick}
                                style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    padding: 0,
                                }}
                                aria-label="Seleccionar mantenciones prepagadas"
                            >
                                {selectedPrepaidMaintenancePlan ? <CheckIcon /> : <AddIcon />}
                            </button>
                        </div>
                    </div>
                )}

                <div className="selector-item">
                    <div className="item-details">
                        <div>
                            <p className="title">Simula Precio de toma de tu usado</p>
                            <p>Si quieres entregar tu vehículo usado, ¡simúlalo ahora!</p>
                        </div>
                        <button
                            onClick={handleUsedCarClick}
                            style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                padding: 0,
                            }}
                            aria-label="Simular precio de vehículo usado"
                        >
                            {usado.range ? <CheckIcon /> : <AddIcon />}
                        </button>
                    </div>
                </div>
            </div>

            <ModalUsados
                isOpen={modalUsadosIsOpen}
                onCloseModal={() => setModalUsadosIsOpen(false)}
            />
            <ModalAccessories
                isOpen={modalAccessoriesIsOpen}
                onCloseModal={() => setModalAccessoriesIsOpen(false)}
            />
            <ModalMaintenancePlans
                isOpen={modalMaintenancePlansIsOpen}
                onCloseModal={() => setModalMaintenancePlansIsOpen(false)}
            />
        </div>
    );
}
